/**
 * useResolveRealTimeModeMutation — direct gateway call from the web app.
 *
 * The auto-detect / loading / match-state path runs through the
 * extension service worker. THIS hook is for the disambiguation path:
 * when the user picks a candidate from the `multiple` state, we re-call
 * `/real-time-mode/resolve` directly with `confirmedEnterPatientId` set,
 * then write the returned `MatchState` into the RTM store.
 *
 * Why not go through the service worker for confirmations?
 *   - The web app is the source of the user interaction (click).
 *   - We can use the existing user-auth Bearer flow instead of the
 *     extension's public-API-key flow.
 *   - One fewer hop, fewer postMessage round-trips, simpler error surface.
 */

import { useCallback } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';
import type { EnterPatient, MatchState, ObservedContext } from '@ctrl-enter/types';
import { tsrClient } from '../api/client';
import {
  useRealTimeModeSetMatch,
  useRealTimeModeSetLoading,
  useRealTimeModePickCandidate,
} from '../stores/real-time-mode-store';

const logger = createLogger('client:hook:rtm-resolve');

export function useResolveRealTimeModeMutation() {
  const setMatch = useRealTimeModeSetMatch();
  const setLoading = useRealTimeModeSetLoading();
  const pickCandidate = useRealTimeModePickCandidate();

  const mutation = tsrClient.realTimeMode.resolve.useMutation({ retry: false });

  /**
   * Resolve again with a chosen candidate. Pre-emptively flips the
   * sidebar into loading state so the user gets feedback before the
   * round-trip completes, then writes the gateway's typed response into
   * the store.
   */
  const confirmCandidate = useCallback(
    async (context: ObservedContext, candidate: EnterPatient) => {
      // Optimistic loading so the picker shows a spinner immediately.
      setLoading(context);
      pickCandidate(candidate);

      try {
        const result = await mutation.mutateAsync({
          body: {
            context,
            confirmedEnterPatientId: candidate.enterPatientId,
          },
        });
        if (result.status === 200) {
          setMatch(result.body.match as MatchState);
        } else {
          setMatch({
            status: 'error',
            context,
            error: `Resolve failed (${result.status})`,
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        logger.error('[rtm-resolve] confirmCandidate failed:', message);
        setMatch({ status: 'error', context, error: message });
      }
    },
    [mutation, setLoading, setMatch, pickCandidate]
  );

  return { confirmCandidate, isPending: mutation.isPending };
}
